import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Divider, Stack, Typography } from '@mui/material';
import { StringParam, useQueryParams } from 'use-query-params';

import { FolderType } from '@/api/generated';
import BackdropLoader from '@/components/BackdropLoader';
import ErrorAccessDenied from '@/components/ErrorAccessDenied/ErrorAccessDenied';
import ErrorGeneric from '@/components/ErrorGeneric/ErrorGeneric';
import Field from '@/components/fields/Field';
import Slider from '@/components/fields/Slider';
import { PermissionGuard } from '@/components/PermissionGuard';
import { getMonthsYearsScalesByDay } from '@/containers/OrganizationSettings/dateOptions';
import NormsList from '@/containers/OrganizationSettings/NormsList';
import { useIsMidotUser } from '@/hooks/permissions/useIsMidotUser';
import { PermissionKeys } from '@/hooks/permissions/usePermissions';
import { useSuborganizationPermissions } from '@/hooks/permissions/useSuborganizationPermissions';
import { ReadonlyProvider } from '@/hooks/useReadonly';
import typography from '@/theme/typography';

const URL_CONFIG_MAP = {
  locationSlug: StringParam
};

const NormsTab: FC = () => {
  const { t } = useTranslation(['settings', 'common']);
  const { organizationSlug } = useParams() as { organizationSlug: string };
  const [{ locationSlug }] = useQueryParams(URL_CONFIG_MAP);

  const suborganizationPermissionVariables = useMemo(
    () => ({
      organizationSlug,
      locationSlug: locationSlug ?? undefined,
      locationType: FolderType.SUB_ORGANIZATION
    }),
    [locationSlug, organizationSlug]
  );

  const { value: { canUpdateIdentificationAndNorms } = {} } = useSuborganizationPermissions({
    variables: suborganizationPermissionVariables
  });

  const { isMidotUser } = useIsMidotUser();
  const dateScales = useMemo(() => getMonthsYearsScalesByDay(t), [t]);

  if (!locationSlug) {
    return null;
  }

  return (
    <PermissionGuard
      all={[PermissionKeys.SuborganizationcanViewIdentificationAndNorms]}
      variables={suborganizationPermissionVariables}
      accessDeniedFallback={<ErrorAccessDenied />}
      errorFallback={<ErrorGeneric />}
      loadingFallback={<BackdropLoader />}
    >
      <ReadonlyProvider readonly={!canUpdateIdentificationAndNorms}>
        <Stack
          width="100%"
          sx={{
            '& .MuiFormLabel-root': { textAlign: 'start' },
            '& .MuiFormControl-root': { pt: 0, px: 0 },
            '& .DropdownSelect-expandMoreIcon': { color: 'text.primary' }
          }}
        >
          <Stack alignItems="start" justifyContent="space-between" width="100%" p={3} pb={0} gap={3}>
            <Typography sx={{ ...typography.body1, fontWeight: typography.fontWeightMedium }}>
              {t('orgSettings.tabs.norms')}
            </Typography>
          </Stack>
          <Stack alignItems="start" justifyContent="space-between" width="100%" p={3}>
            <Typography sx={{ ...typography.body2 }}>{t('orgSettings.sections.validation')}</Typography>
          </Stack>
          <Stack maxWidth={370} mx={3}>
            <Field
              component={Slider}
              name="testValidityDays"
              label={t('orgSettings.fields.accessValidFor')}
              innerProps={{
                min: 30,
                max: 3650,
                valueLabelDisplay: 'auto',
                scales: dateScales,
                disabled: !isMidotUser
              }}
              withFormControl
            />
          </Stack>
          <Divider sx={{ mx: 3 }} orientation="horizontal" />
          <Stack alignItems="start" justifyContent="space-between" width="100%" p={3}>
            <Typography variant="body2">{t('orgSettings.sections.norms')}</Typography>
          </Stack>
          <NormsList />
        </Stack>
      </ReadonlyProvider>
    </PermissionGuard>
  );
};

export default NormsTab;
